import {Alert} from 'src/design/atoms/Alert';
import {Center} from 'src/design/atoms/Center';
import {Grid} from 'src/design/atoms/Grid';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {DatePickerInput} from '@mantine/dates';
import type {DateValue} from '@mantine/dates';
import {DragDropContext} from '@hello-pangea/dnd';
import type {DropResult} from '@hello-pangea/dnd';
import {
  IconAlertTriangle,
  IconCalendar,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react';
import React, {useMemo, useReducer, useState} from 'react';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {ActionIcon} from 'src/design/atoms/ActionIcon';
import {Button} from 'src/design/atoms/Button';
import {SegmentedControl} from 'src/design/atoms/SegmentedControl';
import {Pill} from 'src/design/atoms/Pill';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SidebarLinkInspections} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {
  useCommitDispatchMutation,
  useGetDispatchBoardQuery,
} from 'src/redux/queries/inspection';
import {useInspectionPermissions} from 'src/shared/auth/permissions/useInspectionPermissions';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import type {DispatchView} from './dispatchCalendar';
import {
  WEEK_DEFAULT_HOUR,
  atHour,
  shiftAnchor,
  windowFor,
} from './dispatchCalendar';
import {TIMELINE_MODE_OPTIONS} from '../InspectionList/inspectionTimelineGrouping';
import {
  buildCommitRequest,
  initialTentativeState,
  tentativeCount,
  tentativeReducer,
} from './dispatchTentativeReducer';
import type {DispatchCardMeta} from './components/DispatchCard';
import {DispatchCard} from './components/DispatchCard';
import type {EffectivePlacement} from './components/DispatchCalendarGrid';
import {
  DispatchCalendarGrid,
  parseSlotId,
} from './components/DispatchCalendarGrid';
import type {DispatchQueueEntry} from './components/DispatchQueuePanel';
import {
  DispatchQueuePanel,
  QUEUE_DROPPABLE_ID,
} from './components/DispatchQueuePanel';
import {DispatchScheduleDialog} from './components/DispatchScheduleDialog';

const todayUtcNaive = (): Date => {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
};

// The DatePickerInput reads/writes in local time; bridge to the UTC-naive
// anchor so its display day matches the calendar's UTC day.
const anchorToLocal = (anchor: Date): Date =>
  new Date(anchor.getUTCFullYear(), anchor.getUTCMonth(), anchor.getUTCDate());

const localToAnchor = (value: Date): Date =>
  new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));

export const DispatchCalendarPage: React.FC = () => {
  useSetDocumentTitle('Dispatch');
  const perms = useInspectionPermissions();
  const {setAlert} = useAlert();

  const canManage = perms.manage || perms.manageAll;
  const [view, setView] = useState<DispatchView>('day');
  const [anchor, setAnchor] = useState<Date>(todayUtcNaive);
  const [queueSearch, setQueueSearch] = useState('');
  const [tentative, dispatchTentative] = useReducer(
    tentativeReducer,
    initialTentativeState
  );
  const [highlightedId, setHighlightedId] = useState<number>();
  const [isDragging, setIsDragging] = useState(false);

  // One dialog serves both flows; a preset assignee means "reschedule".
  const [dialogTarget, setDialogTarget] = useState<{
    id: number;
    assigneeId?: number;
    scheduledStartAt?: Date;
  }>();

  const boardArgs = useMemo(() => {
    const {fromUtc, toUtc} = windowFor(anchor, view);
    return {fromUtc: fromUtc.toISOString(), toUtc: toUtc.toISOString()};
  }, [anchor, view]);

  const {
    data: board,
    isLoading,
    isError,
  } = useGetDispatchBoardQuery(boardArgs, {skip: !canManage});

  const [commitDispatch, {isLoading: isCommitting}] =
    useCommitDispatchMutation();

  const metaById = useMemo(() => {
    const map = new Map<number, DispatchCardMeta>();
    board?.queue.forEach(q =>
      map.set(q.id, {
        id: q.id,
        siteName: q.siteName,
        inspectionTypeName: q.inspectionTypeName,
        dueAt: q.dueAt ? parseUtcDate(q.dueAt) : undefined,
      })
    );
    board?.committed.forEach(c =>
      map.set(c.id, {
        id: c.id,
        siteName: c.siteName,
        inspectionTypeName: c.inspectionTypeName,
        dueAt: c.dueAt ? parseUtcDate(c.dueAt) : undefined,
      })
    );
    return map;
  }, [board]);

  const committedIds = useMemo(
    () => new Set((board?.committed ?? []).map(c => c.id)),
    [board]
  );

  const isPastDue = (meta: DispatchCardMeta, scheduled: Date): boolean =>
    !!meta.dueAt && scheduled.getTime() > meta.dueAt.getTime();

  const placements = useMemo<EffectivePlacement[]>(() => {
    const result: EffectivePlacement[] = [];
    // Tentative placements come from either source; metaById covers both.
    for (const [id, change] of Object.entries(tentative.changes)) {
      if (change.type !== 'place') continue;
      const meta = metaById.get(Number(id));
      if (!meta) continue;
      result.push({
        meta,
        assigneeId: change.assigneeId,
        scheduledStartAt: change.scheduledStartAt,
        isTentative: true,
        isPastDue: isPastDue(meta, change.scheduledStartAt),
      });
    }
    // Committed items without a tentative change keep their saved placement.
    board?.committed.forEach(c => {
      if (tentative.changes[c.id]) return;
      const meta = metaById.get(c.id);
      if (!meta || !c.assigneeId || !c.scheduledStartAt) return;
      const scheduled = parseUtcDate(c.scheduledStartAt);
      result.push({
        meta,
        assigneeId: c.assigneeId,
        scheduledStartAt: scheduled,
        isTentative: false,
        isPastDue: isPastDue(meta, scheduled),
      });
    });
    return result;
  }, [board, metaById, tentative]);

  const queueEntries = useMemo<DispatchQueueEntry[]>(() => {
    const now = todayUtcNaive();
    const needle = queueSearch.toLowerCase();
    const entryFrom = (meta: DispatchCardMeta): DispatchQueueEntry => ({
      id: meta.id,
      siteName: meta.siteName,
      inspectionTypeName: meta.inspectionTypeName,
      dueAt: meta.dueAt,
      isPastDue: !!meta.dueAt && meta.dueAt.getTime() < now.getTime(),
    });
    const matches = (meta: DispatchCardMeta): boolean =>
      `${meta.siteName} ${meta.inspectionTypeName}`
        .toLowerCase()
        .includes(needle);
    const entries: DispatchQueueEntry[] = [];
    board?.queue.forEach(q => {
      if (tentative.changes[q.id]?.type === 'place') return;
      const meta = metaById.get(q.id);
      if (meta && matches(meta)) entries.push(entryFrom(meta));
    });
    board?.committed.forEach(c => {
      if (tentative.changes[c.id]?.type !== 'unassign') return;
      const meta = metaById.get(c.id);
      if (meta && matches(meta)) entries.push(entryFrom(meta));
    });
    return entries;
  }, [board, metaById, tentative, queueSearch]);

  const onDragEnd = (result: DropResult) => {
    setIsDragging(false);
    const {draggableId, destination} = result;
    if (!destination) return;
    const inspectionId = Number(draggableId);

    if (destination.droppableId === QUEUE_DROPPABLE_ID) {
      const change = tentative.changes[inspectionId];
      if (change?.type === 'place') {
        dispatchTentative({type: 'remove', inspectionId});
      } else if (committedIds.has(inspectionId)) {
        dispatchTentative({type: 'unassign', inspectionId});
      }
      return;
    }

    const slot = parseSlotId(destination.droppableId);
    if (!slot) return;
    const [y, m, d] = slot.dayKey.split('-').map(Number);
    const hour = view === 'week' ? WEEK_DEFAULT_HOUR : slot.hour;
    const scheduledStartAt = atHour(new Date(Date.UTC(y, m - 1, d)), hour);
    dispatchTentative({
      type: 'place',
      inspectionId,
      assigneeId: slot.lane,
      scheduledStartAt,
    });
  };

  const handleCommit = async () => {
    setHighlightedId(undefined);
    try {
      await commitDispatch(buildCommitRequest(tentative)).unwrap();
      dispatchTentative({type: 'reset'});
      setAlert('Dispatch changes were saved.', 'success');
    } catch (error) {
      const message = parseErrorMessage(error);
      setAlert(message, 'error');
      // Server names the offending inspection id; highlight it if present.
      const offending = Object.keys(tentative.changes)
        .map(Number)
        .find(id => message.includes(String(id)));
      setHighlightedId(offending);
    }
  };

  const renderCard = (placement: EffectivePlacement) => (
    <DispatchCard
      meta={placement.meta}
      scheduledStartAt={placement.scheduledStartAt}
      isTentative={placement.isTentative}
      isPastDue={placement.isPastDue}
      isHighlighted={highlightedId === placement.meta.id}
      showTimeControl={view === 'day'}
      onSetTime={(hour, minute) => {
        const scheduledStartAt = atHour(
          placement.scheduledStartAt,
          hour,
          minute
        );
        if (placement.isTentative) {
          dispatchTentative({
            type: 'setTime',
            inspectionId: placement.meta.id,
            scheduledStartAt,
          });
        } else {
          dispatchTentative({
            type: 'place',
            inspectionId: placement.meta.id,
            assigneeId: placement.assigneeId,
            scheduledStartAt,
          });
        }
      }}
      onReschedule={() =>
        setDialogTarget({
          id: placement.meta.id,
          assigneeId: placement.assigneeId,
          scheduledStartAt: placement.scheduledStartAt,
        })
      }
      onUnassign={() =>
        dispatchTentative({type: 'unassign', inspectionId: placement.meta.id})
      }
      onRemoveTentative={() =>
        dispatchTentative({type: 'remove', inspectionId: placement.meta.id})
      }
    />
  );

  const pendingCount = tentativeCount(tentative);
  const inspectors = board?.inspectors ?? [];

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
      <Stack gap="md">
        <BackBar
          fallbackTo={PathUtility.Inspections('manage')}
          fallbackLabel="Back to Inspections"
          breadcrumbs={[
            {label: 'Inspections', to: PathUtility.Inspections('manage')},
            {label: 'Dispatch'},
          ]}
        />

        <PageHeader>
          <PageHeader.Title icon={SidebarLinkInspections.icon}>
            Dispatch
          </PageHeader.Title>
          {canManage && (
            <PageHeader.Actions>
              <SegmentedControl
                value={view}
                onChange={value => setView(value as DispatchView)}
                data={TIMELINE_MODE_OPTIONS}
              />
              <Group gap="xs" wrap="nowrap">
                <ActionIcon
                  variant="default"
                  tooltipText="Previous"
                  onClick={() => setAnchor(a => shiftAnchor(a, view, -1))}>
                  <IconChevronLeft size={16} />
                </ActionIcon>
                <DatePickerInput
                  size="xs"
                  w={150}
                  aria-label="Anchor date"
                  valueFormat="MMM D, YYYY"
                  value={anchorToLocal(anchor)}
                  onChange={(value: DateValue) => {
                    if (value) setAnchor(localToAnchor(new Date(value)));
                  }}
                  leftSection={<IconCalendar size={16} />}
                />
                <ActionIcon
                  variant="default"
                  tooltipText="Next"
                  onClick={() => setAnchor(a => shiftAnchor(a, view, 1))}>
                  <IconChevronRight size={16} />
                </ActionIcon>
                <Button
                  variant="subtle"
                  color="gray"
                  size="xs"
                  onClick={() => setAnchor(todayUtcNaive())}>
                  Today
                </Button>
              </Group>
              {pendingCount > 0 && (
                <Pill
                  text={`${pendingCount} unconfirmed change${
                    pendingCount === 1 ? '' : 's'
                  }`}
                  color="yellow"
                />
              )}
            </PageHeader.Actions>
          )}
        </PageHeader>

        {!canManage ? (
          <Alert
            color="red"
            variant="light"
            icon={<IconAlertTriangle size={18} />}
            title="Access denied">
            You need the Manage claim to use the dispatch calendar.
          </Alert>
        ) : isLoading ? (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        ) : isError || !board ? (
          <Alert
            color="red"
            variant="light"
            icon={<IconAlertTriangle size={18} />}
            title="Could not load the dispatch board">
            Something went wrong loading inspections. Go back and try again.
          </Alert>
        ) : (
          <DragDropContext
            onDragStart={() => setIsDragging(true)}
            onDragEnd={onDragEnd}>
            <Grid gutter="md">
              <Grid.Col span={{base: 12, md: 3}}>
                <DispatchQueuePanel
                  entries={queueEntries}
                  search={queueSearch}
                  onSearchChange={setQueueSearch}
                  isDragActive={isDragging}
                  onSchedule={id =>
                    setDialogTarget({id, scheduledStartAt: anchor})
                  }
                />
              </Grid.Col>
              <Grid.Col span={{base: 12, md: 9}} style={{minWidth: 0}}>
                {inspectors.length === 0 ? (
                  <Alert
                    color="gray"
                    variant="light"
                    title="No inspector lanes">
                    You have no team members to dispatch to. Inspectors you
                    supervise will appear here as lanes.
                  </Alert>
                ) : (
                  <DispatchCalendarGrid
                    view={view}
                    anchor={anchor}
                    lanes={inspectors}
                    placements={placements}
                    renderCard={renderCard}
                    isDragActive={isDragging}
                    onDrillDay={day => {
                      setAnchor(day);
                      setView('day');
                    }}
                  />
                )}
              </Grid.Col>
            </Grid>
          </DragDropContext>
        )}
      </Stack>

      {pendingCount > 0 && (
        <Paper
          withBorder
          p="sm"
          shadow="md"
          style={{position: 'sticky', bottom: 0, zIndex: 10}}>
          <Group justify="space-between">
            <Text size="sm" fw={600}>
              {pendingCount} unconfirmed change{pendingCount === 1 ? '' : 's'}
            </Text>
            <Group gap="sm">
              <Button
                variant="subtle"
                color="gray"
                onClick={() => dispatchTentative({type: 'reset'})}>
                Discard all
              </Button>
              <Button loading={isCommitting} onClick={handleCommit}>
                Save {pendingCount} change{pendingCount === 1 ? '' : 's'}
              </Button>
            </Group>
          </Group>
        </Paper>
      )}

      <DispatchScheduleDialog
        opened={dialogTarget !== undefined}
        onClose={() => setDialogTarget(undefined)}
        title={
          dialogTarget?.assigneeId !== undefined
            ? 'Reschedule inspection'
            : 'Schedule inspection'
        }
        inspectors={inspectors}
        initialAssigneeId={dialogTarget?.assigneeId}
        initialScheduledStartAt={dialogTarget?.scheduledStartAt}
        onSubmit={result => {
          if (dialogTarget) {
            dispatchTentative({
              type: 'place',
              inspectionId: dialogTarget.id,
              assigneeId: result.assigneeId,
              scheduledStartAt: result.scheduledStartAt,
            });
          }
          setDialogTarget(undefined);
        }}
      />
    </ContentWrapper>
  );
};

// Route gates on View; the Manage claim is enforced in-component (custom
// operations aren't expressible on the route config).
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.InspectionsDispatch(),
  element: <DispatchCalendarPage />,
  module: ModuleEnum.Inspections,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});
